/* SPDX-License-Identifier: AGPL-3.0-or-later */

import { NgTemplateOutlet } from '@angular/common';
import { Component, Input, TemplateRef } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { KoStandingDto } from '@api';
import { KoStandingDtoWithParentInfo } from '../../dtos/KoStandingDtoWithParentInfo';

@Component({
  selector: 'app-knockout-phase-tree-node',
  standalone: true,
  imports: [NgTemplateOutlet, MatCardModule],
  templateUrl: './knockout-phase-tree-node.component.html',
  styleUrl: './knockout-phase-tree-node.component.scss',
})
export class KnockoutPhaseTreeNodeComponent {
  @Input()
  node?: KoStandingDto;

  get flattenedTree() {
    if (!this.node) {
      return [];
    }

    const findDepth = ({ preceedingStandings: [head] = [] }: KoStandingDto, depth = 1): number =>
      head ? findDepth(head, depth + 1) : depth;
    const flatten = (
      node: KoStandingDtoWithParentInfo,
      column: number,
      row: number,
    ): { column: number; row: number; node: KoStandingDtoWithParentInfo }[] => {
      const [left, right] = node.preceedingStandings ?? [];
      if (!left || !right) {
        return [{ column, row, node }];
      }

      return [
        { column, row, node },
        ...flatten(
          { ...left, parentMatchHasTableAssigned: node.table !== null },
          column - 1,
          Math.floor(row - maxNumberOfRows / 2 ** (depth - (column - 1)) / 2),
        ),
        ...flatten(
          { ...right, parentMatchHasTableAssigned: node.table !== null },
          column - 1,
          Math.ceil(row + maxNumberOfRows / 2 ** (depth - (column - 1)) / 2),
        ),
      ];
    };

    const depth = findDepth(this.node);
    const maxNumberOfRows = 2 ** depth;
    const nodes = flatten(
      { ...this.node, parentMatchHasTableAssigned: false },
      depth,
      Math.ceil((2 ** (depth - 1) * 2 - 1) / 2),
    );

    return nodes;
  }

  @Input({ required: true })
  template!: TemplateRef<KoStandingDtoWithParentInfo>;

  protected getNodeStyle = (column: number, row: number) =>
    column === 2 ? `grid-area: ${row - 1} / ${column}/${row + 2}` : `grid-area: ${row} / ${column}`;
}
